import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogTitle,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { Button } from '@codes/ui-kit/components/ui/button';
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle } from '@codes/ui-kit/components/ui/sheet';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { PromptBody, PromptView } from '@lemoncloud/ssocio-chatbots-api';
import { useQueryClient } from '@tanstack/react-query';
import { Change, diffLines } from 'diff';
import { Eye, EyeOff, X } from 'lucide-react';

import { useCreatePrompt } from '@codes/agents';
import { useUpdatePrompt } from '@codes/chatbots';
import { formatDate } from '@codes/utils';

import {
    updateCacheWithDeleted,
    updateCacheWithNewPrompt,
    updateRevisionCacheWithDeleted,
    updateRevisionCacheWithNew,
} from '../utils';

interface PromptRevisionDetailSheetProps {
    revision: PromptView | null;
    currentPrompt?: PromptView | null;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onApplyRevision?: (revision: PromptView) => void;
    onDeleteRevision?: (revision: PromptView) => void;
    onCreateRevision?: (revision: PromptView) => void;
}

export const PromptRevisionDetailSheet: React.FC<PromptRevisionDetailSheetProps> = ({
    revision,
    currentPrompt,
    isOpen,
    onOpenChange,
    onApplyRevision,
    onDeleteRevision,
    onCreateRevision,
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [showDiff, setShowDiff] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const createPromptMutation = useCreatePrompt();
    const updatePromptMutation = useUpdatePrompt();

    const handleCreate = async () => {
        if (!revision || !currentPrompt) {
            return;
        }

        try {
            const promptData: PromptBody = {
                name: revision.name || '',
                stereo: currentPrompt.stereo,
                contents: revision.contents || [],
            };

            const result = await createPromptMutation.mutateAsync(promptData);
            updateCacheWithNewPrompt(queryClient, 'prompts', result as PromptView);

            // 새 프롬프트가 생성되면 revision 캐시에도 추가
            if (result && currentPrompt.id) {
                updateRevisionCacheWithNew(queryClient, currentPrompt.id, result as PromptView);
            }

            toast({
                title: t(
                    'prompt.form.revisions.detail.messages.createSuccess.title',
                    '프롬프트가 성공적으로 생성되었습니다'
                ),
                description: t(
                    'prompt.form.revisions.detail.messages.createSuccess.description',
                    '새 프롬프트가 추가되었습니다'
                ),
            });

            // 부모 컴포넌트에 새 revision 생성 알림
            onCreateRevision?.(result as PromptView);

            onOpenChange(false);
        } catch (error) {
            console.error('Error creating prompt:', error);
            toast({
                title: t('prompt.form.revisions.detail.messages.createError.title', '프롬프트 생성 실패'),
                description: t(
                    'prompt.form.revisions.detail.messages.createError.description',
                    '프롬프트 생성 중 오류가 발생했습니다'
                ),
                variant: 'destructive',
            });
        }
    };

    const handleDelete = async () => {
        if (!revision?.id || !currentPrompt?.id) {
            return;
        }

        try {
            await updatePromptMutation.mutateAsync({
                promptId: revision.id,
                hidden: true,
            });
            updateCacheWithDeleted(queryClient, 'prompts', revision.id.toString());

            // revision 캐시에서도 삭제
            updateRevisionCacheWithDeleted(queryClient, currentPrompt.id, revision.id.toString());

            toast({
                title: t('prompt.form.revisions.detail.messages.deleteSuccess.title', '프롬프트가 삭제되었습니다'),
                description: t(
                    'prompt.form.revisions.detail.messages.deleteSuccess.description',
                    '프롬프트가 성공적으로 삭제되었습니다'
                ),
            });

            setIsDeleteDialogOpen(false);
            onOpenChange(false);

            // Notify parent component about the deletion
            onDeleteRevision?.(revision);
        } catch (error) {
            console.error('Error deleting prompt:', error);
            toast({
                title: t('prompt.form.revisions.detail.messages.deleteError.title', '프롬프트 삭제 실패'),
                description: t(
                    'prompt.form.revisions.detail.messages.deleteError.description',
                    '프롬프트 삭제 중 오류가 발생했습니다'
                ),
                variant: 'destructive',
            });
        }
    };

    const handleApply = () => {
        if (revision) {
            onApplyRevision?.(revision);
            onOpenChange(false);
        }
    };

    const handleDeleteClick = () => {
        if (revision) {
            setIsDeleteDialogOpen(true);
        }
    };

    const formatRevisionDate = (dateString: string) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}. ${month}. ${day}`;
    };

    // Get diff using diff library
    const currentContent = currentPrompt?.contents?.join('\n') || '';
    const revisionContent = revision?.contents?.join('\n') || '';

    const diffResult = showDiff && currentPrompt ? diffLines(currentContent, revisionContent) : null;

    return (
        <>
            <Sheet open={isOpen} onOpenChange={onOpenChange}>
                <SheetContent className="w-[500px] max-md:w-full overflow-hidden flex flex-col">
                    <SheetHeader>
                        <SheetTitle>{t('prompt.form.revisions.detail.title', '수정 내역')}</SheetTitle>
                        <SheetClose asChild>
                            <X className="w-5 h-5" />
                        </SheetClose>
                    </SheetHeader>
                    {/* Revision Date */}
                    {revision && (
                        <div className="mb-3">
                            <div className="font-medium text-text mb-1">
                                {t('prompt.form.revisions.detail.modifiedDate', '수정일')}
                            </div>
                            <div className="text-sub">{formatDate(revision.createdAt)}</div>
                        </div>
                    )}

                    {/* Prompt Name */}
                    {revision && (
                        <div className="mb-4">
                            <div className="font-medium text-text mb-1">
                                {t('prompt.form.revisions.detail.promptName', '프롬프트 이름')}
                            </div>
                            <div className="text-sub">
                                {revision.name || t('prompt.form.revisions.detail.noName', '프롬프트 이름으로 노출')}
                            </div>
                        </div>
                    )}

                    <div className="flex-1 overflow-hidden flex flex-col">
                        {revision ? (
                            <>
                                <div className="flex-1 overflow-hidden flex flex-col">
                                    <div className="mb-3">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="font-medium text-text">
                                                {revision.stereo === 'system'
                                                    ? t('prompt.form.revisions.detail.systemPrompt', 'System Prompt')
                                                    : revision.stereo === 'user'
                                                    ? t('prompt.form.revisions.detail.userPrompt', 'User Prompt')
                                                    : t('prompt.form.revisions.detail.content', 'System Prompt')}
                                            </div>
                                            {currentPrompt && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setShowDiff(!showDiff)}
                                                    className="h-6 px-2 text-xs text-sub hover:text-text"
                                                >
                                                    {showDiff ? (
                                                        <>
                                                            <EyeOff className="w-3 h-3 mr-1" />
                                                            {t(
                                                                'prompt.form.revisions.detail.hideDiff',
                                                                '차이점 숨기기'
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Eye className="w-3 h-3 mr-1" />
                                                            {t('prompt.form.revisions.detail.showDiff', '차이점 보기')}
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                        </div>

                                        {showDiff && diffResult ? (
                                            <div className="bg-card border border-border rounded-lg p-3 text-xs leading-relaxed overflow-auto max-h-[300px] min-h-[200px]">
                                                {diffResult.map((change: Change, index: number) => {
                                                    // Split the change value into lines to handle multi-line changes
                                                    const lines = change.value.split('\n');
                                                    // Filter out empty last line that results from splitting
                                                    const filteredLines =
                                                        lines[lines.length - 1] === '' ? lines.slice(0, -1) : lines;

                                                    return filteredLines.map((line: string, lineIndex: number) => (
                                                        <div
                                                            key={`${index}-${lineIndex}`}
                                                            className={`${
                                                                change.added
                                                                    ? 'bg-green-100 text-green-800 border-l-2 border-green-400 pl-2'
                                                                    : change.removed
                                                                    ? 'bg-red-100 text-red-800 border-l-2 border-red-400 pl-2'
                                                                    : 'text-sub-title'
                                                            } py-1`}
                                                        >
                                                            <span className="inline-block w-4 text-xs text-text-400 mr-2">
                                                                {change.added ? '+' : change.removed ? '-' : ' '}
                                                            </span>
                                                            <span className="whitespace-pre-wrap font-mono text-xs">
                                                                {line}
                                                            </span>
                                                        </div>
                                                    ));
                                                })}
                                            </div>
                                        ) : (
                                            <div className="bg-card border border-border rounded-lg p-3 text-sm text-sub-title leading-relaxed overflow-auto max-h-[300px] min-h-[200px]">
                                                <pre className="whitespace-pre-wrap font-sans">
                                                    {revision.contents?.join('\n') || ''}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-col gap-2 p-4 flex-shrink-0 items-center justify-center">
                                    <Button
                                        onClick={handleCreate}
                                        disabled={createPromptMutation.isPending}
                                        className="w-full h-10 bg-text hover:bg-text/90 text-white text-sm font-medium rounded-lg"
                                    >
                                        {createPromptMutation.isPending ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                                {t('common.loading', '처리 중...')}
                                            </>
                                        ) : (
                                            t('prompt.form.revisions.detail.addAsNewPrompt', '새 프롬프트로 추가하기')
                                        )}
                                    </Button>
                                    <Button
                                        onClick={handleDeleteClick}
                                        disabled={updatePromptMutation.isPending}
                                        variant="ghost"
                                        className="w-full h-10 text-error hover:text-error hover:bg-error/5 text-sm font-medium rounded-lg"
                                    >
                                        {updatePromptMutation.isPending ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-error border-t-transparent rounded-full animate-spin mr-2" />
                                                {t('common.loading', '처리 중...')}
                                            </>
                                        ) : (
                                            t('prompt.form.revisions.detail.deletePrompt', '프롬프트 삭제하기')
                                        )}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center text-sub">
                                    <div className="text-lg font-medium mb-2">
                                        {t('prompt.form.revisions.detail.noRevision', '선택된 수정 내역이 없습니다')}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </SheetContent>
            </Sheet>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogTitle>
                        {t('prompt.form.revisions.detail.dialog.title', '프롬프트 삭제')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {t(
                            'prompt.form.revisions.detail.dialog.warning',
                            '이 프롬프트를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.'
                        )}
                    </AlertDialogDescription>
                    <AlertDialogFooter>
                        <AlertDialogCancel type="button" onClick={() => setIsDeleteDialogOpen(false)}>
                            {t('common.cancel', '취소')}
                        </AlertDialogCancel>
                        <Button onClick={handleDelete} disabled={updatePromptMutation.isPending} className="w-full">
                            {updatePromptMutation.isPending ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    {t('common.loading', '처리 중...')}
                                </>
                            ) : (
                                t('prompt.form.revisions.detail.dialog.actions.delete', '삭제')
                            )}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
